import { useState } from "react";
import { api, ZEFFY } from "../../lib/api";
import { toast } from "sonner";
import { ArrowRight, ExternalLink, MapPin, Bed, Plane } from "lucide-react";

const TABLES = [
  { id: "vip-4", label: "VIP Table for 4", price: "$999", perks: ["Front-row seating", "Premium bar service", "Custom signage", "Welcome gifts"] },
  { id: "vip-8", label: "VIP Table for 8", price: "$2,500", perks: ["Front-row seating", "Premium bar service", "Custom signage", "Welcome gifts", "Step-and-repeat moment"] },
  { id: "corp", label: "Corporate Table", price: "$5,000", perks: ["Reserved seating for 8", "Program acknowledgement", "Standard bar service"] },
  { id: "presenting", label: "Presenting Sponsor", price: "$10,000", perks: ["Tier-dependent placement", "Logo at table", "See sponsorship tiers"] },
];

export function VIPTable() {
  const [selected, setSelected] = useState(TABLES[0].id);
  const [form, setForm] = useState({
    full_name: "", organization: "", email: "", phone: "",
    table_type: TABLES[0].label, guest_count: 8, dietary_restrictions: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handle = (k) => (e) => {
    const v = k === "guest_count" ? Number(e.target.value) : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const pick = (t) => {
    setSelected(t.id);
    const guestCount = t.id === "vip-4" ? 4 : t.id === "presenting" ? 10 : 8;
    setForm({ ...form, table_type: t.label, guest_count: guestCount });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/vip/reservation", form);
      toast.success("VIP table request received. Our concierge will confirm details shortly.");
      setForm({ ...form, full_name: "", email: "", phone: "", dietary_restrictions: "", notes: "" });
    } catch {
      toast.error("Could not submit reservation.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="vip"
      data-testid="vip-section"
      className="relative py-24 md:py-32 scroll-mt-nav"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 items-end mb-12">
          <div className="md:col-span-8">
            <p className="text-overline text-aawta-pink mb-4">VIP TABLE RESERVATIONS</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.05]">
              Reserve your<br /> place at the table.
            </h2>
          </div>
          <a
            href={ZEFFY.vipTable}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="vip-zeffy-btn"
            className="btn-pink md:col-span-4 md:justify-self-end"
          >
            RESERVE VIA EVENTBRITE <ExternalLink size={14} />
          </a>
        </div>

        <div className="grid md:grid-cols-4 gap-3 mb-10">
          {TABLES.map((t) => (
            <button
              key={t.id}
              data-testid={`vip-table-${t.id}`}
              onClick={() => pick(t)}
              className={`text-left p-6 border transition-all ${
                selected === t.id
                  ? "border-aawta-pink bg-aawta-pink/5"
                  : "border-white/10 hover:border-white/30"
              }`}
            >
              <p className="text-overline text-white/40 mb-3">{t.id.toUpperCase()}</p>
              <p className="font-display text-2xl text-white">{t.label}</p>
              <p className="font-mono text-aawta-pink mt-1">{t.price}</p>
              <ul className="mt-5 space-y-1.5 text-xs text-white/60">
                {t.perks.map((p) => <li key={p}>· {p}</li>)}
              </ul>
            </button>
          ))}
        </div>

        <form onSubmit={submit} data-testid="vip-form" className="glass-panel p-8 md:p-10 grid md:grid-cols-12 gap-6">
          <div className="md:col-span-6">
            <label className="label-luxe">Host Name *</label>
            <input data-testid="vip-name" required className="input-luxe" value={form.full_name} onChange={handle("full_name")} />
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Organization</label>
            <input data-testid="vip-org" className="input-luxe" value={form.organization} onChange={handle("organization")} />
          </div>
          <div className="md:col-span-4">
            <label className="label-luxe">Email *</label>
            <input data-testid="vip-email" required type="email" className="input-luxe" value={form.email} onChange={handle("email")} />
          </div>
          <div className="md:col-span-4">
            <label className="label-luxe">Phone</label>
            <input data-testid="vip-phone" className="input-luxe" value={form.phone} onChange={handle("phone")} />
          </div>
          <div className="md:col-span-4">
            <label className="label-luxe">Guests *</label>
            <input data-testid="vip-guests" type="number" min={1} max={12} required className="input-luxe" value={form.guest_count} onChange={handle("guest_count")} />
          </div>
          <div className="md:col-span-12">
            <label className="label-luxe">Dietary Restrictions</label>
            <input data-testid="vip-dietary" className="input-luxe" value={form.dietary_restrictions} onChange={handle("dietary_restrictions")} />
          </div>
          <div className="md:col-span-12">
            <label className="label-luxe">Notes / Guest List Highlights</label>
            <textarea data-testid="vip-notes" rows={3} className="input-luxe resize-none" value={form.notes} onChange={handle("notes")} />
          </div>
          <button type="submit" data-testid="vip-submit" disabled={submitting} className="btn-pink md:col-span-12 disabled:opacity-50">
            {submitting ? "RESERVING..." : "REQUEST TABLE RESERVATION"} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </section>
  );
}

export function Hotels() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    arrival: "", departure: "", rooms: 1, guests: 1, notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handle = (k) => (e) => {
    const v = k === "rooms" || k === "guests" ? Number(e.target.value) : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Pack room/guest counts into notes so the existing /hotels/interest schema accepts them.
      const extra = `Rooms: ${form.rooms} · Guests: ${form.guests}` + (form.phone ? ` · Phone: ${form.phone}` : "");
      const payload = {
        full_name: form.full_name,
        email: form.email,
        arrival: form.arrival,
        departure: form.departure,
        notes: form.notes ? `${form.notes}\n${extra}` : extra,
      };
      await api.post("/hotels/interest", payload);
      toast.success("Thanks! Our concierge team will follow up with hotel block details.");
      setForm({ full_name: "", email: "", phone: "", arrival: "", departure: "", rooms: 1, guests: 1, notes: "" });
    } catch {
      toast.error("Could not submit your request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="hotels"
      data-testid="hotels-section"
      className="relative py-24 md:py-32 bg-aawta-charcoal scroll-mt-nav"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 items-end mb-12">
          <div className="md:col-span-7">
            <p className="text-overline text-aawta-pink mb-4">HOTELS &amp; TRAVEL</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.05]">
              Stay where the<br /> evening begins.
            </h2>
          </div>
          <p className="md:col-span-5 text-white/60 leading-relaxed">
            Atlanta &middot; December 9&ndash;11, 2026. Register your interest below and
            we&rsquo;ll share hotel block details, discounted group rates, and booking
            instructions as soon as rooms open.
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="hotel-interest-form"
          className="glass-panel p-8 md:p-10 grid md:grid-cols-12 gap-6"
        >
          <div className="md:col-span-12 flex items-center gap-3 mb-2">
            <Bed className="text-aawta-pink" size={20} />
            <p className="text-overline text-aawta-pink">HOTEL INTEREST FORM</p>
          </div>

          <div className="md:col-span-6">
            <label className="label-luxe">Full Name *</label>
            <input data-testid="hotel-name" required className="input-luxe" value={form.full_name} onChange={handle("full_name")} />
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Email *</label>
            <input data-testid="hotel-email" required type="email" className="input-luxe" value={form.email} onChange={handle("email")} />
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Phone</label>
            <input data-testid="hotel-phone" className="input-luxe" value={form.phone} onChange={handle("phone")} />
          </div>
          <div className="md:col-span-3">
            <label className="label-luxe">Rooms Needed *</label>
            <input data-testid="hotel-rooms" required type="number" min={1} max={30} className="input-luxe" value={form.rooms} onChange={handle("rooms")} />
          </div>
          <div className="md:col-span-3">
            <label className="label-luxe">Guests *</label>
            <input data-testid="hotel-guests" required type="number" min={1} max={60} className="input-luxe" value={form.guests} onChange={handle("guests")} />
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Check-In Date *</label>
            <input data-testid="hotel-arrival" required type="date" className="input-luxe" value={form.arrival} onChange={handle("arrival")} />
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Check-Out Date *</label>
            <input data-testid="hotel-departure" required type="date" className="input-luxe" value={form.departure} onChange={handle("departure")} />
          </div>
          <div className="md:col-span-12">
            <label className="label-luxe">Notes / Special Requests</label>
            <textarea data-testid="hotel-notes" rows={3} className="input-luxe resize-none" value={form.notes} onChange={handle("notes")} />
          </div>

          <div className="md:col-span-12 flex flex-wrap items-center justify-between gap-3">
            <p className="text-overline text-white/40 inline-flex items-center gap-2">
              <MapPin size={11} className="text-aawta-pink" /> ATLANTA · DEC 9–11, 2026
              <span className="text-white/30">·</span>
              <Plane size={11} className="text-aawta-pink" /> NEAR HARTSFIELD-JACKSON
            </p>
            <button type="submit" data-testid="hotel-submit" disabled={submitting} className="btn-pink disabled:opacity-50">
              {submitting ? "SUBMITTING…" : "REGISTER INTEREST"} <ArrowRight size={14} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
